import { BrowserRouter, Navigate, Route, Routes } from "react-router";
import { tripPhase } from "@shared/time.ts";
import { BookingDetailPage, BookingsPage } from "../features/bookings/BookingsPage.tsx";
import { DayPage, DriverPage, EventPage, FlightPage, StayPage, TransferPage } from "../features/details/DetailPages.tsx";
import { ComposePage, BookingsAddPage } from "../features/editor/ComposePage.tsx";
import { StayEditPage } from "../features/editor/StayEditPage.tsx";
import { EditorPage } from "../features/editor/EditorPage.tsx";
import { CardsPage, EmergencyPage, EssentialsPage, MoneyPage, PhonePage, PrintPage, ReviewPage } from "../features/essentials/EssentialsPage.tsx";
import { MoneyHomePage } from "../features/money/MoneyPage.tsx";
import { JourneyPage } from "../features/journey/JourneyPage.tsx";
import { LockScreen } from "../features/lock/LockScreen.tsx";
import { MapPage } from "../features/map/MapPage.tsx";
import { SearchPage } from "../features/search/SearchPage.tsx";
import { TodayPage } from "../features/today/TodayPage.tsx";
import { UpdatesPage } from "../features/updates/UpdatesPage.tsx";
import { ShowCardPage, ShowDriverPage } from "../components/ShowCardPage.tsx";
import { AppShell } from "./shell.tsx";
import { ScreenErrorBoundary } from "../components/ScreenErrorBoundary.tsx";
import { useSession } from "./session.tsx";
import { useTrip, useTripClock } from "./trip.tsx";

function HomeRedirect() {
  const { trip } = useTrip();
  const now = useTripClock();
  if (!trip) return <Navigate to="/journey" replace />;
  const phase = tripPhase(now, trip.trip.startDate, trip.trip.endDate, trip.trip.homeTimezone);
  return <Navigate to={phase === "during" ? "/today" : "/journey"} replace />;
}

export function App() {
  const { loading, authenticated } = useSession();
  if (loading) {
    return (
      <main className="page">
        <div className="skeleton" style={{ height: 220, marginBottom: 16 }} />
        <div className="skeleton" style={{ height: 48 }} />
      </main>
    );
  }

  return (
    <BrowserRouter>
      {authenticated ? <AppRoutes /> : <LockScreen />}
    </BrowserRouter>
  );
}

function AppRoutes() {
  return (
      <Routes>
        <Route path="/show/:phraseId" element={<ShowCardPage />} />
        <Route path="/show/driver/:id" element={<ShowDriverPage />} />
        <Route element={<AppShell />}>
          <Route path="/" element={<HomeRedirect />} />
          <Route path="/today" element={<TodayPage />} />
          <Route path="/updates/:id" element={<UpdatesPage />} />
          <Route path="/updates" element={<UpdatesPage />} />
          <Route path="/journey" element={<JourneyPage />} />
          <Route path="/journey/stay/:id" element={<StayPage />} />
          <Route path="/journey/stay/:id/edit" element={<StayEditPage />} />
          <Route path="/journey/day/:date" element={<DayPage />} />
          <Route path="/journey/day/:date/add" element={<ComposePage />} />
          <Route path="/journey/flight/:id" element={<FlightPage />} />
          <Route path="/journey/transfer/:id" element={<TransferPage />} />
          <Route path="/journey/event/:id" element={<EventPage />} />
          <Route path="/driver/:id" element={<DriverPage />} />
          <Route path="/map" element={<MapPage />} />
          <Route path="/money" element={<ScreenErrorBoundary title="Money needs a correction"><MoneyHomePage /></ScreenErrorBoundary>} />
          <Route path="/money/:id" element={<ScreenErrorBoundary title="This booking's money needs a correction"><BookingDetailPage /></ScreenErrorBoundary>} />
          <Route path="/bookings/add" element={<BookingsAddPage />} />
          <Route path="/bookings" element={<BookingsPage />} />
          <Route path="/bookings/costs" element={<Navigate to="/money" replace />} />
          <Route path="/bookings/:id" element={<BookingDetailPage />} />
          <Route path="/essentials/allergy" element={<Navigate to="/essentials/cards" replace />} />
          <Route path="/essentials" element={<EssentialsPage />} />
          <Route path="/essentials/cards" element={<CardsPage />} />
          <Route path="/essentials/emergency" element={<EmergencyPage />} />
          <Route path="/essentials/phone" element={<PhonePage />} />
          <Route path="/essentials/money" element={<MoneyPage />} />
          <Route path="/essentials/review" element={<ReviewPage />} />
          <Route path="/essentials/print" element={<PrintPage />} />
          <Route path="/editor" element={<EditorPage />} />
          <Route path="/search" element={<SearchPage />} />
        </Route>
      </Routes>
  );
}
